import { formatCount } from "./numbers";

/**
 * The add-ons sold per PERSON (ADR-0015 addenda of 2026-10-01, 2026-10-02 and 2026-10-08): Staff per
 * employee, Student Attendance per student and Labour Attendance per labourer. Each is a prepaid quantity
 * bound to the rest of the subscription by the same three rules — their quantities TOGETHER stay inside the
 * people capacity (rule 2, combined since 2026-10-02), a people reduction carries them down together, and
 * none may fall below the people of its kind who are active (rule 3). This is the portal's copy of the
 * server's `PERSON_UNIT_KINDS`
 * (`@nacs/shared` `dto/platform.ts`), keyed by the catalogue's `unitKind` — never by an item code — with
 * the words each kind is said in (M20 Z-30, Z-D61).
 *
 * The sentences are pure and take numbers only, on purpose: the api's refusals reach `describeError` with
 * nothing but their `details`, so the add-on's name is the kind's own word here rather than the catalogue's.
 */
export const PERSON_KINDS = [
  {
    unitKind: "employee",
    /** `limits.<resource>` on the entitlements: `{ used, limit }` while the add-on is live. */
    resource: "employees",
    /** The key `PERSON_CAPACITY_EXCEEDED` names this kind's quantity by. */
    capacityKey: "staff",
    noun: "employee",
    nouns: "employees",
    /** The add-on, as a sentence about a change names it. */
    product: "Staff",
    /** The kind, in the combined rule's sentence: "people across Staff and Students". */
    group: "Staff",
    /** What frees a slot: "exit employees", "exit the employee". */
    leave: "exit",
    belowActiveCode: "STAFF_QUANTITY_BELOW_ACTIVE",
  },
  {
    unitKind: "student",
    resource: "students",
    capacityKey: "students",
    noun: "student",
    nouns: "students",
    product: "Student Attendance",
    group: "Students",
    leave: "withdraw",
    belowActiveCode: "STUDENT_QUANTITY_BELOW_ACTIVE",
  },
  {
    // M25 (ADR-0015 addendum of 2026-10-08, rules 3–4): Labour Attendance per active labourer. The third kind
    // of the combined rule — Staff + Student Attendance + Labour Attendance together stay inside the people
    // capacity. `belowActiveCode` is the server's (`PERSON_UNIT_KINDS`, S8-01).
    unitKind: "labourer",
    resource: "labourers",
    capacityKey: "labourers",
    noun: "labourer",
    nouns: "labourers",
    product: "Labour Attendance",
    group: "Labour",
    leave: "release",
    belowActiveCode: "LABOUR_QUANTITY_BELOW_ACTIVE",
  },
] as const;

export type PersonKind = (typeof PERSON_KINDS)[number];
export type PersonUnitKind = PersonKind["unitKind"];

/** The per-person kind of a catalogue `unitKind`, or undefined for terminals, people lots and flat add-ons. */
export function personKindOfUnit(unitKind: string | undefined): PersonKind | undefined {
  return PERSON_KINDS.find((kind) => kind.unitKind === unitKind);
}

/** The kind a rule-3 refusal code is about (`STAFF_`/`STUDENT_QUANTITY_BELOW_ACTIVE`). */
export function personKindOfBelowActiveCode(code: string): PersonKind | undefined {
  return PERSON_KINDS.find((kind) => kind.belowActiveCode === code);
}

/** "80 employees", "1 student". */
export function personCount(kind: PersonKind, count: number): string {
  return `${formatCount(count)} ${count === 1 ? kind.noun : kind.nouns}`;
}

const capitalise = (text: string) => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

/** "a", "a and b", "a, b and c". */
export function andList(parts: readonly string[]): string {
  return parts.length <= 1 ? (parts[0] ?? "") : `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;
}

/**
 * Rule 3 — a per-person quantity may never fall below the people of its kind who are active (`409
 * STAFF_`/`STUDENT_QUANTITY_BELOW_ACTIVE`): "80 employees are active; exit employees before reducing Staff to
 * 50.", "42 students are active; withdraw students before reducing Student Attendance to 30." Asking for none
 * at all is cancelling, and the sentence says so rather than "reducing … to 0".
 */
export function personBelowActiveSentence(kind: PersonKind, active: number, requested: number): string {
  const who = active === 1 ? `1 ${kind.noun} is` : `${formatCount(active)} ${kind.nouns} are`;
  const leave = active === 1 ? `${kind.leave} the ${kind.noun}` : `${kind.leave} ${kind.nouns}`;
  const what =
    requested === 0 ? `cancelling ${kind.product}` : `reducing ${kind.product} to ${formatCount(requested)}`;
  return `${who} active; ${leave} before ${what}.`;
}

/** Rule 3's title: "Staff cannot go below the employees who are active". */
export function personBelowActiveTitle(kind: PersonKind): string {
  return `${kind.product} cannot go below the ${kind.nouns} who are active`;
}

/** Rule 3 without the api's numbers: "Withdraw students first, then reduce Student Attendance again." */
export function personBelowActiveFallback(kind: PersonKind): string {
  return `${capitalise(kind.leave)} ${kind.nouns} first, then reduce ${kind.product} again.`;
}

/** One per-person quantity a people reduction carries down: which kind, and what it falls to. */
export interface CarriedQuantity {
  kind: PersonKind;
  to: number;
}

/**
 * Rule 2, the carry — said BEFORE the owner confirms a people reduction that the per-person quantities no
 * longer fit inside: "Staff will drop to 250 employees with this change.", and with both kinds carried
 * "Staff will drop to 150 employees and Student Attendance to 100 students with this change."
 */
export function personCarriedSentence(carried: readonly CarriedQuantity[]): string {
  const [first, ...rest] = carried;
  if (!first) return "";
  const parts = [
    `${first.kind.product} will drop to ${personCount(first.kind, first.to)}`,
    ...rest.map((c) => `${c.kind.product} to ${personCount(c.kind, c.to)}`),
  ];
  return `${andList(parts)} with this change.`;
}

/** The per-person quantities `PERSON_CAPACITY_EXCEEDED` names, by `capacityKey` (`staff`, `students`). */
export type PersonQuantities = Readonly<Partial<Record<PersonKind["capacityKey"], number>>>;

const peopleCount = (count: number) => `${formatCount(count)} ${count === 1 ? "person" : "people"}`;

/** The kinds a refusal names: those with a quantity above zero, in `PERSON_KINDS` order. */
function namedKinds(quantities: PersonQuantities): Array<{ kind: PersonKind; quantity: number }> {
  return PERSON_KINDS.flatMap((kind) => {
    const quantity = quantities[kind.capacityKey] ?? 0;
    return quantity > 0 ? [{ kind, quantity }] : [];
  });
}

/** Rule 2's title: "Staff cannot be more than the people your plan holds", or both kinds named. */
export function personCapacityTitle(quantities: PersonQuantities): string {
  const named = namedKinds(quantities);
  const who = named.length > 0 ? andList(named.map((n) => n.kind.product)) : "Staff and students";
  return `${who} cannot be more than the people your plan holds`;
}

/**
 * Rule 2, refused (`422 PERSON_CAPACITY_EXCEEDED`, and M19's `QUANTITY_OUT_OF_RANGE` with `peopleCapacity`):
 * every number, and the two ways out. One kind reads as Staff always has — "Staff is 501 employees, but this
 * plan holds 500 people. …" — and two add up: "Staff (60 employees) and Student Attendance (50 students) are
 * 110 people together, but this plan holds 100 people. Add terminals or extra people first, or choose fewer."
 */
export function personCapacitySentence(peopleCapacity: number, quantities: PersonQuantities): string {
  const named = namedKinds(quantities);
  const holds = `this plan holds ${peopleCount(peopleCapacity)}`;
  const [only] = named;
  if (named.length === 1 && only) {
    return `${only.kind.product} is ${personCount(only.kind, only.quantity)}, but ${holds}. Add terminals or extra people first, or choose fewer ${only.kind.nouns}.`;
  }
  if (named.length === 0) {
    return `Staff and students together cannot be more than the people your plan holds. Add terminals or extra people first.`;
  }
  const total = named.reduce((sum, n) => sum + n.quantity, 0);
  const parts = named.map((n) => `${n.kind.product} (${personCount(n.kind, n.quantity)})`);
  return `${andList(parts)} are ${peopleCount(total)} together, but ${holds}. Add terminals or extra people first, or choose fewer.`;
}
